import { WhiteboardPermission, WhiteboardStatus } from "@src/gql/graphql";
import type z from "zod";
import type { WhiteboardWithUsersSchema } from "./schema";

export function determineOwnership(
	whiteboard: z.infer<typeof WhiteboardWithUsersSchema>,
	currentUserEmail: string | null,
): "owned" | "shared" | "published" {
	// if the top permission is owner, then the whiteboard is owned
	if (whiteboard.permission === WhiteboardPermission.Owner) {
		return "owned";
	}

	// if the user is in the list of collaborators then the whiteboard is shared
	if (
		currentUserEmail !== null &&
		whiteboard.collaboration?.collaborators.some(
			(collaborator) => collaborator.userEmail === currentUserEmail,
		)
	) {
		return "shared";
	}

	return "published";
}

export function determineEditable(
	whiteboard: z.infer<typeof WhiteboardWithUsersSchema>,
	ownership: "owned" | "shared" | "published",
	enabled: boolean,
) {
	// if the board is published then we need to check for writeable permission
	// this means that if the user isn't logged in then we ignore the enabled flag
	if (ownership === "published") {
		return (
			(whiteboard.permission === WhiteboardPermission.Write ||
				whiteboard.permission === WhiteboardPermission.InvokeAi) &&
			whiteboard.status === WhiteboardStatus.Active
		);
	}

	// otherwise we need to check if the feature is enabled for this account and the permissions are write or owner
	if (enabled) {
		const isWriteable =
			whiteboard.permission === WhiteboardPermission.Write ||
			whiteboard.permission === WhiteboardPermission.Owner ||
			whiteboard.permission === WhiteboardPermission.InvokeAi;
		const isActive = whiteboard.status === WhiteboardStatus.Active;
		return isWriteable && isActive;
	}

	// otherwise we cannot edit the board
	return false;
}

export function generateWhiteboardName(num: number) {
	const currentDate = new Date();

	const monthNames = new Intl.DateTimeFormat(undefined, { month: "short" })
		.formatToParts(new Date())
		.find((part) => part.type === "month")?.value
		? Array.from(
				{ length: 12 },
				(_, i) =>
					new Intl.DateTimeFormat(undefined, { month: "short" })
						.formatToParts(new Date(2000, i, 1))
						.find((part) => part.type === "month")?.value || "",
			)
		: [
				"Jan",
				"Feb",
				"Mar",
				"Apr",
				"May",
				"Jun",
				"Jul",
				"Aug",
				"Sep",
				"Oct",
				"Nov",
				"Dec",
			];

	const day = currentDate.getDate();
	const month = monthNames[currentDate.getMonth()];
	const year = currentDate.getFullYear();

	const newWhiteboardName = `${day} ${month} ${year} #${num}`;
	return newWhiteboardName;
}

/**
 * Next `#N` suffix for default board/card names.
 * Uses the trailing `#digits` (not the first digits in the name — e.g. the
 * day in `2 Nov 2026 #31`), then falls back to list length.
 */
export function getNextNamedItemIndex(
	items: ReadonlyArray<{ name?: string | null }> | undefined,
	highestIndexRef?: { current: number },
): number {
	let maxIndex = items?.length ?? 0;
	if (items) {
		for (const item of items) {
			const match = item.name?.match(/#(\d+)\s*$/);
			if (match) {
				const num = Number.parseInt(match[1], 10);
				if (!Number.isNaN(num) && num > maxIndex) {
					maxIndex = num;
				}
			}
		}
	}
	const baseline = highestIndexRef?.current ?? 0;
	const nextIndex = Math.max(maxIndex, baseline) + 1;
	if (highestIndexRef) {
		highestIndexRef.current = nextIndex;
	}
	return nextIndex;
}
